import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

// Components/Hooks
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';
import CardGridOrder from './components/cardGrid/cardGrid';
import KogModalSideComponent from '@/main/components/modal/ModalSide';
import ModalTracking from './components/modalTracking/modalTracking';
import FiltersOrderScreen from './filtersOrderScreen/filtersOrderScreen';

// Api's
import { procurementApi, quoteApi } from '@/main/api';

// Models
import {
  GetItemsBySupplierRequest
} from '@/main/models/request';
import {
  GetItemsBySupplierResponse,
  QuoteDataResponse
} from '@/main/models/response';
import { QuoteCountStatusRequest, QuoteFilterRequest } from '@/main/models/request/quoteRequest';

// Utils & Enums
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import {
  getDefaultMessageResultsFound,
  getTranslateByKey,
  isNullUndefined
} from '@/main/services/utils';
import { initalDateForm } from '@/main/services/utilsDate';
import { ParameterTypeEnum } from '@/main/services/enum/ParameterTypeEnum';
import { withQueryparams } from '@/main/components/core/queryParams/queryparams';

// Redux & Hooks
import {
  getQuoteStatus
} from '@/main/store/quoteStatus';
import {
  resetReducerBodyshops
} from '@/main/store/bodyshop';
import {
  resetReducerInsurers
} from '@/main/store/insurer';
import { getCountriesUser } from '@/main/store/country';
import {
  resetReducerSuppliers
} from '@/main/store/supplier';
import { getQuoteCountStatus } from '@/main/store/quoteCountStatus';
import { useParameterByTypeScreenReducer } from '@/main/store/parameterByTypeScreen';

// Styles
import * as S from './styles';

export interface IGridData {
  data: QuoteDataResponse[];
  totalItems: number;
  isLoading: boolean;
}
export const INITIAL_GRID_STATE_ORDER_SCREEN: IGridData = {
  totalItems: 0,
  isLoading: false,
  data: []
};
export interface IGridDataTracking {
  data: GetItemsBySupplierResponse[];
  totalItems: number;
  isLoading: boolean;
  orderNumber: string | null;
}

const IS_USER_CONTROL = true;

function OrderScreen() {
  // Reducers
  const dispatch = useDispatch();
  const parameterRangeDays = useParameterByTypeScreenReducer(ParameterTypeEnum.DefaultSearchOrderDaysInterval);

  // States
  const INITIAL_GRID_TRACKING_STATE: IGridDataTracking = {
    totalItems: 0,
    isLoading: false,
    data: [],
    orderNumber: null
  };
  const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE_ORDER_SCREEN);
  const [gridDataTracking, setGridDataTracking] = useState<IGridDataTracking>(
    INITIAL_GRID_TRACKING_STATE
  );
  const [formFilters, setFormFilters] = useState<QuoteFilterRequest | null>(
    null
  );
  const [hasShowMore, setHasShowMore] = useState<boolean>(false);
  const [
    formFiltersTracking,
    setFormFiltersTracking
  ] = useState<GetItemsBySupplierRequest | null>(null);
  const [hasShowMoreTracking, setHasShowMoreTracking] = useState<boolean>(
    false
  );
  const [pageIndex, setPageIndex] = useState<number>(
    PaginationEnum.PageIndexIntial
  );
  const [pageIndexTracking, setPageIndexTracking] = useState<number>(
    PaginationEnum.PageIndexIntial
  );
  const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState<
    boolean
  >(false);
  const [
    isLoadingInfiniteScrollTracking,
    setIsLoadingInfiniteScrollTracking
  ] = useState<boolean>(false);
  const [modalTracking, setModalTracking] = useState<boolean>(false);
  const [isLoadingModalTracking, setIsLoadingModalTracking] = useState<boolean>(
    false
  );
  const totalNumberOfRecordsRef = useRef<number>(0);

  // Methods/Validatoin/Variables for Form

  const titleGrid = getDefaultMessageResultsFound(
    gridData.data.length,
    gridData.totalItems
  );

  function onLoadInfiniteScroll(request: QuoteFilterRequest, refresh: boolean = false) {
    setIsLoadingInfiniteScroll(true);
    quoteApi
      .getSearchGrid(request)
      .then(response => {
        let resultData: QuoteDataResponse[] = [];
        let resultTotalItems: number = 0;
        if (
          response.data &&
          response.data.results &&
          response.data.results.length > 0
        ) {
          resultData = response.data.results;
          resultTotalItems = response.data.totalNumberOfRecords || totalNumberOfRecordsRef.current;
        }

        if (resultTotalItems > 0) {
          totalNumberOfRecordsRef.current = resultTotalItems;
        }

        if (gridData.data.length > 0 && !refresh) {
          resultData = [...gridData.data, ...resultData];
        }
        setGridData({
          isLoading: false,
          totalItems: totalNumberOfRecordsRef.current,
          data: resultData
        });

        // Disable show more
        if (
          totalNumberOfRecordsRef.current < PaginationEnum.PageSize30 ||
          totalNumberOfRecordsRef.current === resultData.length
        ) {
          setHasShowMore(false);
        }
      })
      .finally(() => setIsLoadingInfiniteScroll(false));
  }

  function onLoadInfiniteScrollTracking(request: GetItemsBySupplierRequest) {
    setIsLoadingInfiniteScrollTracking(true);
    procurementApi
      .getItemsBySupplier(request)
      .then(response => {
        let resultData: GetItemsBySupplierResponse[] = [];
        let resultTotalItems: number = 0;
        if (
          response.data &&
          response.data.results &&
          response.data.results.length > 0
        ) {
          resultData = response.data.results;
          resultTotalItems = response.data.totalNumberOfRecords;
        }
        if (gridDataTracking.data.length > 0) {
          resultData = [...gridDataTracking.data, ...resultData];
        }
        setGridDataTracking({
          isLoading: false,
          totalItems: resultTotalItems,
          data: resultData,
          orderNumber: null
        });

        // Disable show more
        if (
          resultTotalItems < PaginationEnum.PageSize5 ||
          resultTotalItems === resultData.length
        ) {
          setHasShowMoreTracking(false);
        }
      })
      .finally(() => setIsLoadingInfiniteScrollTracking(false));
  }

  function onLoadSearchGrid() {
    const index = pageIndex + PaginationEnum.PageIndexIntial;
    setPageIndex(index);
    const formFiltersOnLoadSearchGrid = formFilters;
    if (formFiltersOnLoadSearchGrid) {
      const updatedFormFilters = {
        ...formFiltersOnLoadSearchGrid,
        pageIndex: index,
        totalNumberOfRecords: totalNumberOfRecordsRef.current
      };
      onLoadInfiniteScroll(updatedFormFilters);
    }
  }

  function refreshGrid() {
    const formFiltersRefreshGrid = formFilters;
    if (formFiltersRefreshGrid) {
      totalNumberOfRecordsRef.current = 0;
      setGridData({ data: [], isLoading: true, totalItems: 0 })
      onLoadInfiniteScroll(formFiltersRefreshGrid, true);
    }
  }

  function onLoadTracking() {
    const index = pageIndexTracking + PaginationEnum.PageIndexIntial;
    setPageIndexTracking(index);
    const formFiltersOnLoadTracking = formFiltersTracking;
    if (formFiltersOnLoadTracking) {
      const updatedFormFilters = {
        ...formFiltersOnLoadTracking,
        pageIndex: index
      };
      onLoadInfiniteScrollTracking(updatedFormFilters);
    }
  }

  // Initial load default domains
  useEffect(() => {
    dispatch(getCountriesUser());
    dispatch(getQuoteStatus(IS_USER_CONTROL));
    return () => {
      dispatch(resetReducerBodyshops());
      dispatch(resetReducerInsurers());
      dispatch(resetReducerSuppliers());
    }
  }, []);

  // Load Domains by Country

  function handleOpenModalTrancking({
    guidQuoteProcurement
  }: GetItemsBySupplierRequest, orderNumber?: string) {
    setIsLoadingModalTracking(true);
    const request = {
      guidQuoteProcurement,
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize5
    };
    setFormFiltersTracking(request);
    procurementApi
      .getItemsBySupplier(request)
      .then(response => {
        let resultData: GetItemsBySupplierResponse[] = [];
        let resultTotalItems: number = 0;
        if (
          response.data &&
          response.data.results &&
          response.data.results.length > 0
        ) {
          resultData = response.data.results;
          resultTotalItems = response.data.totalNumberOfRecords;
        }
        if (gridDataTracking.data.length > 0) {
          resultData = [...gridDataTracking.data, ...resultData];
        }
        setGridDataTracking({
          isLoading: false,
          totalItems: resultTotalItems,
          data: resultData,
          orderNumber: orderNumber || null
        });

        setHasShowMoreTracking(
          response.data.results.length < response.data.totalNumberOfRecords
        );
        setModalTracking(true);
      })
      .catch(() => { })
      .finally(() => setIsLoadingModalTracking(false));
  }

  function handleUpdateQuoteGridStatus(
    guid: string,
    newStatus: number,
    newStatusSupplierName: string
  ) {
    if (gridData.data) {
      let updatedData: QuoteDataResponse[] = [];
      gridData.data.map(item => {
        if (item.guidQuoteProcurement === guid) {
          return updatedData.push({
            ...item,
            idStatus: newStatus,
            nameStatus: String(newStatusSupplierName)
          });
        }
        return updatedData.push(item);
      });

      const newGrid = {
        ...gridData,
        data: updatedData
      };

      setGridData(newGrid);
    }
  }


  function handleUpdateQuoteCountStatus(startDate?: Date | null, finalDate?: Date | null) {
    if (!isNullUndefined(parameterRangeDays)) {
      const request: QuoteCountStatusRequest = {
        startDate: initalDateForm(parameterRangeDays),
        finalDate: initalDateForm()
      };

      dispatch(getQuoteCountStatus(request));
    }
  }

  return (
    <ContainerScreen
      title={`
      ${getTranslateByKey('information.home')}
      ${' > '}
      ${getTranslateByKey('screen.orderSearchOrder')} `}
      style={{ minHeight: '90%' }}>
      <S.Container>
        <FiltersOrderScreen
          setPageIndex={setPageIndex}
          setIsLoadingInfiniteScroll={setIsLoadingInfiniteScroll}
          setHasShowMore={setHasShowMore}
          setFormFilters={setFormFilters}
          setGridData={setGridData}
          handleUpdateQuoteCountStatus={handleUpdateQuoteCountStatus}
          onTotalNumberOfRecordsLoaded={(total) => { totalNumberOfRecordsRef.current = total; }}
        />
        <CardGridOrder
          title={titleGrid}
          formFilters={formFilters}
          gridData={gridData}
          infiniteScrollConfig={{
            dataLength: gridData.data.length,
            fetchData: () => onLoadSearchGrid(),
            hasMore: hasShowMore,
            isLoading: isLoadingInfiniteScroll
          }}
          handleOpenModalTrancking={handleOpenModalTrancking}
          handleUpdateQuoteCountStatus={handleUpdateQuoteCountStatus}
          handleUpdateQuoteGridStatus={handleUpdateQuoteGridStatus}
          setGridData={setGridData}
          refreshGrid={refreshGrid}
        />
      </S.Container>

      <KogModalSideComponent
        open={modalTracking}
        closeModal={() => {
          setModalTracking(false);
          setGridDataTracking(INITIAL_GRID_TRACKING_STATE);
          setPageIndexTracking(PaginationEnum.PageIndexIntial);
        }}
        padding="20px 0px 20px 20px">
        <S.TitleModal>{getTranslateByKey('information.tracking')}</S.TitleModal>
        <ModalTracking
          isLoadingModalTracking={isLoadingModalTracking}
          packTracking={gridDataTracking.data}
          infiniteScrollConfig={{
            dataLength: gridDataTracking.data.length,
            fetchData: () => onLoadTracking(),
            hasMore: hasShowMoreTracking,
            isLoading: isLoadingInfiniteScrollTracking,
            idScrollTarget: 'id-modal-scroll'
          }}
          orderNumber={gridDataTracking.orderNumber}
        />
      </KogModalSideComponent>
    </ContainerScreen>
  );
}

export default withQueryparams(OrderScreen);
